/**
 * 운영자 고객 상세의 「기사 기본 사진」 — 기사를 광고주에게 보낼 때 사진이 없으면 이 사진이 먼저 붙는다
 * (261012-client-article-photos). 없으면 예전처럼 지난 승인 기사의 사진이 붙는다.
 * ★ 지워도 이미 기사에 붙은 사진은 그대로다 — 다음에 보내는 기사부터 바뀐다.
 */
import {
  ARTICLE_PHOTO_MAX_BYTES,
  ARTICLE_PHOTO_MAX_COUNT,
  ARTICLE_PHOTO_TYPES,
  inferIndustry,
  isMedicalIndustry,
  MEDICAL_ARTICLE_PHOTOS,
} from '@geo-admin/shared/contract/api/article.ts';
import { article } from '@geo-admin/shared/contract/api/index.ts';
import type { BrandDetail } from '@geo-admin/shared/contract/api/registry.ts';
import { type ChangeEvent, useState } from 'react';
import { useApi, useApiMutation } from '../lib/api.ts';
import { readBase64 } from './ArticleParts.tsx';
import { Card, CardBody, CardHeader } from './ui/card.tsx';

const MAX_MB = ARTICLE_PHOTO_MAX_BYTES / 1024 / 1024;

/** 사진 파일 주소 — <img> 가 헤더를 못 실어 쿼리로 운영자 영역을 알린다(middleware/session.ts) */
const fileUrl = (brandId: string, photoId: string) => `/api/brands/${brandId}/article-photos/${photoId}?realm=admin`;

export function BrandArticlePhotosAdmin({ brand: c }: { brand: BrandDetail }) {
  const list = useApi(article.listBrandArticlePhotos, { params: { id: c.id } });
  const upload = useApiMutation(article.uploadBrandArticlePhoto);
  const remove = useApiMutation(article.deleteBrandArticlePhoto);
  const [error, setError] = useState<string | null>(null);
  const photos = list.data?.photos ?? [];
  const busy = upload.isPending || remove.isPending;
  // 업종은 고객에 따로 없다 — 기사 작성과 같이 업체명으로 정한다
  const industry = inferIndustry(c.name);
  const medical = industry !== null && isMedicalIndustry(industry);

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    setError(null);
    if (photos.length + files.length > ARTICLE_PHOTO_MAX_COUNT) {
      return setError(`사진은 ${ARTICLE_PHOTO_MAX_COUNT}장까지 올릴 수 있습니다`);
    }
    for (const f of files) {
      if (!(ARTICLE_PHOTO_TYPES as readonly string[]).includes(f.type))
        return setError(`${f.name}: JPG·PNG·WEBP 사진만 올릴 수 있습니다`);
      if (f.size > ARTICLE_PHOTO_MAX_BYTES) return setError(`${f.name}: 한 장에 ${MAX_MB}MB 까지 올릴 수 있습니다`);
    }
    try {
      for (const f of files) {
        await upload.mutateAsync({
          params: { id: c.id },
          body: {
            fileName: f.name,
            contentType: f.type as (typeof ARTICLE_PHOTO_TYPES)[number],
            dataBase64: await readBase64(f),
          },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onDelete = async (photoId: string, name: string) => {
    if (!confirm(`기본 사진 「${name}」을 지웁니다.\n이미 기사에 붙은 사진은 그대로 남습니다.`)) return;
    setError(null);
    try {
      await remove.mutateAsync({ params: { id: c.id }, query: { photoId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Card>
      <CardHeader
        title="기사 기본 사진"
        description="기사를 광고주에게 보낼 때 이 사진이 먼저 붙습니다. 없으면 지난 승인 기사의 사진이 붙습니다."
        actions={
          <label
            className={`rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-100 ${busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
          >
            {upload.isPending ? '올리는 중…' : '사진 올리기'}
            <input
              type="file"
              accept={ARTICLE_PHOTO_TYPES.join(',')}
              multiple
              className="hidden"
              onChange={(e) => void onFiles(e)}
            />
          </label>
        }
      />
      <CardBody className="space-y-3 text-sm">
        {medical ? (
          <p className="text-slate-500">의료기관은 {MEDICAL_ARTICLE_PHOTOS.join(' · ')}만 언론사가 받습니다.</p>
        ) : null}
        {error ? <p className="text-bad">{error}</p> : null}
        {list.isLoading ? null : list.error ? (
          <p className="text-bad">사진을 불러오지 못했습니다</p>
        ) : photos.length === 0 ? (
          <p className="text-slate-400">등록된 기본 사진 없음</p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {photos.map((p) => {
              const url = fileUrl(c.id, p.id);
              return (
                <li key={p.id} className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                  <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={p.fileName} className="aspect-[4/3] w-full object-contain" />
                  </a>
                  <div className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-slate-600">
                    <span className="truncate" title={p.fileName}>
                      {p.fileName}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      className="shrink-0 text-bad hover:underline disabled:opacity-50"
                      onClick={() => void onDelete(p.id, p.fileName)}
                    >
                      삭제
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
